import React, { useState, useEffect, useRef } from "react";
import { Grid, List, X, SlidersHorizontal } from "lucide-react";
import ResponsiveDropMenu from "../_ResponsiveComponents/DropMenu";

interface ProductHeaderProps {
  totalResults: number;
  resultsPerPage: number;
  currentPage: number;
  sortBy: string;
  viewMode: string;
  loading?: boolean;
  searchQuery?: string | null;
  categoryBreadcrumb?: Array<{ id: number; name: string }>;
  onSortChange: (sortBy: string) => void;
  onViewModeChange: (viewMode: TViewMode) => void;
  onClearSearch?: () => void;
  onFilterToggle?: () => void;
  filterCount?: number;
}

type TViewMode = "grid" | "list";

const ProductHeader: React.FC<ProductHeaderProps> = ({
  totalResults,
  resultsPerPage,
  currentPage,
  sortBy,
  viewMode,
  loading = false,
  searchQuery,
  categoryBreadcrumb = [],
  onSortChange,
  onViewModeChange,
  onClearSearch,
  onFilterToggle,
  filterCount = 0,
}) => {
  // Local sort state so the UI updates immediately while we debounce the parent callback
  const [localSort, setLocalSort] = useState<string>(sortBy);
  const sortTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalSort(sortBy);
  }, [sortBy]);

  useEffect(() => {
    return () => {
      if (sortTimerRef.current) {
        clearTimeout(sortTimerRef.current);
      }
    };
  }, []);

  const handleSortChange = (value: string) => {
    setLocalSort(value);
    if (sortTimerRef.current) clearTimeout(sortTimerRef.current);
    // small delay to avoid rapid duplicate requests and UI jank
    sortTimerRef.current = setTimeout(() => {
      if (value !== sortBy) {
        onSortChange(value);
      }
      sortTimerRef.current = null;
    }, 220);
  };

  const startResult = (currentPage - 1) * resultsPerPage + 1;
  const endResult = Math.min(currentPage * resultsPerPage, totalResults);

  return (
    <div className="bg-neutral-900 p-3 sm:p-4 rounded-lg shadow-sm mb-6 border border-neutral-800">
      {/* Category/Subcategory Display */}
      {!searchQuery && (
        <div className="mb-3 pb-3 border-b">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <i className="ph ph-folder-open text-primary text-lg"></i>
            <span className="text-neutral-400">Category:</span>

            {categoryBreadcrumb && categoryBreadcrumb.length > 0 ? (
              <div className="flex items-center gap-2 flex-wrap">
                {categoryBreadcrumb.map((crumb, index) => (
                  <div key={crumb.id} className="flex items-center gap-2">
                    <span
                      className={`font-medium px-3 py-1 rounded-full transition-colors ${
                        index === categoryBreadcrumb.length - 1
                          ? "font-semibold text-primary"
                          : "text-neutral-300"
                      }`}
                    >
                      {crumb.name}
                    </span>
                    {index < categoryBreadcrumb.length - 1 && (
                      <i className="ph ph-caret-right text-gray-400"></i>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                All Categories
              </span>
            )}
          </div>
        </div>
      )}

      {/* Search Query Display */}
      {searchQuery && (
        <div className="mb-3 p-2 bg-neutral-800 rounded-lg border border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-neutral-300">
                Search results for:
              </span>
              <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                "{searchQuery}"
              </span>
            </div>
            {onClearSearch && (
              <button
                onClick={onClearSearch}
                className="flex items-center space-x-1 text-neutral-400 hover:text-white text-sm font-medium transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Clear</span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2 text-white">
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            )}
            <span className="hidden sm:inline">
              {loading ? (
                "Loading products..."
              ) : (
                <>
                  Showing {totalResults > 0 ? startResult : 0}-
                  {totalResults > 0 ? endResult : 0} of {totalResults} result
                  {totalResults !== 1 ? "s" : ""}
                </>
              )}
            </span>
            <span className="sm:hidden">
              {loading ? (
                "Loading..."
              ) : (
                <>
                  {totalResults} result{totalResults !== 1 ? "s" : ""}
                </>
              )}
            </span>
          </h1>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
          {/* Filter Button for Mobile/Tablet */}
          {onFilterToggle && (
            <button
              onClick={onFilterToggle}
              className="lg:hidden flex items-center gap-2 bg-primary hover:bg-primary-dark text-black px-4 py-2 rounded-lg font-semibold text-sm transition-colors relative"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Filters</span>
              {filterCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {filterCount}
                </span>
              )}
            </button>
          )}

          {/* View Mode Toggle - Hidden on mobile */}
          <div className="hidden sm:flex items-center space-x-2">
            <button
              className={`p-2 rounded ${
                viewMode === "grid"
                  ? "bg-primary text-black"
                  : "bg-neutral-800 text-neutral-400 hover:text-white"
              }`}
              onClick={() => onViewModeChange("grid")}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              className={`p-2 rounded ${
                viewMode === "list"
                  ? "bg-primary text-black"
                  : "bg-neutral-800 text-neutral-400 hover:text-white"
              }`}
              onClick={() => onViewModeChange("list")}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center space-x-2 flex-1 sm:flex-initial">
            <span className="text-sm font-medium text-neutral-400 hidden sm:inline">
              Sort by:
            </span>
            <span className="text-sm font-medium text-neutral-400 sm:hidden">
              Sort:
            </span>
            <ResponsiveDropMenu
              value={localSort}
              onChange={(v) => handleSortChange(String(v))}
              className="flex-1 sm:flex-initial"
              buttonClassName="bg-primary hover:bg-primary-dark text-black border-transparent"
              options={[
                { value: "newest", label: "Newest" },
                { value: "popular", label: "Popular" },
                { value: "price_low_to_high", label: "Price: Low to High" },
                { value: "price_high_to_low", label: "Price: High to Low" },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductHeader;
